/**
 * NemoValidator - A lightweight class-based form validation library
 * @module nemo-validator
 */

/**
 * Form validation class that handles input validation based on CSS classes
 */
class NemoValidator {
  /**
   * Creates a new validator instance
   * @param {string} formClass - CSS class that identifies forms to validate
   * @param {Object} customRules - Optional custom validation rules to extend default rules
   */
  constructor(formClass, customRules = {}) {
    this.formClass = formClass;
    
    this.validationRules = {
      'validate-name': {
        pattern: /^[a-zA-Z .]+$/,
        message: 'Only alphabets and dots are allowed.',
        filterPattern: /[^a-zA-Z .]/g
      },
      'validate-mobile': {
        pattern: /^\d{10}$/,
        message: 'Please enter a valid 10-digit mobile number.',
        filterPattern: /[^\d]/g,
        maxLength: 10
      },
      'validate-aadhar': {
        pattern: /^\d{12}$/,
        message: 'Please enter a valid 12-digit Aadhar number.',
        filterPattern: /[^\d]/g,
        maxLength: 12
      },
      'validate-pan': {
        pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
        message: 'Please enter a valid PAN number (AYTPD0038F format).',
        filterPattern: /[^a-zA-Z0-9]/g,
        maxLength: 10
      },
      'validate-email': {
        pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: 'Please enter a valid email address.',
        filterPattern: /[^a-zA-Z0-9.@_-]/g
      },
      'validate-address': {
        pattern: /^[a-zA-Z0-9 .,/]+$/,
        message: 'Only alphabets, numbers, dots, forward slash and spaces are allowed.',
        filterPattern: /[^a-zA-Z0-9 .,/]/g
      },
      'validate-pin': {
        pattern: /^\d{6}$/,
        message: 'Please enter a valid 6-digit Pin Code.',
        filterPattern: /[^\d]/g,
        maxLength: 6
      },
      'validate-text': {
        pattern: /^[a-zA-Z .,/]+$/,
        message: 'Only alphabets, dots, forward slash and spaces are allowed.',
        filterPattern: /[^a-zA-Z .,/]/g
      },
      'validate-numeric': {
        pattern: /^\d+$/,
        message: 'Only numbers are allowed.',
        filterPattern: /[^\d]/g
      },
      'validate-business': {
        pattern: /^[a-zA-Z0-9 .,/]+$/,
        message: 'Only alphabets, numbers, dots, forward slash and spaces are allowed.',
        filterPattern: /[^a-zA-Z0-9 .,/]/g
      },
      'validate-other': {
        pattern: /^[a-zA-Z0-9 .,/]+$/,
        message: 'Only alphabets, numbers, dots, forward slash and spaces are allowed.',
        filterPattern: /[^a-zA-Z0-9 .,/]/g
      },
      'validate-gst': {
        pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
        message: 'Please enter a valid 15-digit GSTIN number (22AAAAA0000A1Z5).',
        filterPattern: /[^a-zA-Z0-9]/g,
        maxLength: 15
      },
      'validate-uam': {
        pattern: /^[A-Z]{2}[0-9]{2}[A-Z]{1}[0-9]{7}$/,
        message: 'Please enter a valid UAM number (DL05A1234567 format).',
        filterPattern: /[^a-zA-Z0-9]/g,
        maxLength: 12
      },
      'validate-udyam': {
        pattern: /^[A-Z]{5}-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/,
        message: 'Please enter a valid 16 characters UDYAM number. (i.e UDYAM-XX-00-0000000)',
        filterPattern: /[^A-Z0-9-]/g,
        maxLength: 16
      },
      'validate-amount': {
        pattern: /^[0-9]+(\.[0-9]{1,2})?$/,
        message: 'Enter amount up to 2 decimal places',
        filterPattern: /[^0-9.]/g
      },
      'validate-two-decimal': {
        pattern: /^[0-9]+(\.[0-9]{0,2})?$/,
        message: 'Please enter a valid number with up to 2 decimal places',
        filterPattern: /[^0-9.]/g,
        setupEvents: function(element) {
          
          let lastValidValue = '';
          
          const isValidDecimalFormat = (value) => {
            if (!value) return true;
            return /^[0-9]+(\.[0-9]{0,2})?$/.test(value);
          };
          
          
          element.addEventListener('keydown', function(e) {
            
            if (e.key === 'Backspace' || e.key === 'Delete' || 
                e.key === 'ArrowLeft' || e.key === 'ArrowRight' || 
                e.key === 'Tab' || e.key === 'Home' || e.key === 'End' || 
                e.ctrlKey || e.altKey || e.metaKey) {
              return;
            }
            
            
            const value = this.value;
            const start = this.selectionStart;
            const end = this.selectionEnd;
            
            
            const potentialNewValue = value.substring(0, start) + e.key + value.substring(end);
            
            
            if (!isValidDecimalFormat(potentialNewValue)) {
              e.preventDefault();
              return false;
            }
          });
          
          
          element.addEventListener('input', function() {
            if (isValidDecimalFormat(this.value)) {
              lastValidValue = this.value;
            } else {
              
              this.value = lastValidValue;
            }
            
            
            const validator = window.nemoValidatorInstance;
            if (validator) {
              validator.validateField(this);
            }
          });
          
          
          element.addEventListener('paste', function(e) {
            
            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
            const start = this.selectionStart;
            const end = this.selectionEnd;
            
            
            const potentialNewValue = this.value.substring(0, start) + pastedText + this.value.substring(end);
            
            
            if (!isValidDecimalFormat(potentialNewValue)) {
              e.preventDefault();
              
              
              if (potentialNewValue.includes('.')) {
                const parts = potentialNewValue.split('.');
                if (parts.length === 2) {
                  const correctedValue = parts[0] + '.' + parts[1].substring(0, 2);
                  
                  
                  if (isValidDecimalFormat(correctedValue)) {
                    this.value = correctedValue;
                    
                    this.selectionStart = this.selectionEnd = 
                      Math.min(correctedValue.length, start + correctedValue.length - potentialNewValue.length);
                    
                    
                    this.dispatchEvent(new Event('input', { bubbles: true }));
                  }
                }
              }
            }
          });
          
          
          element.addEventListener('focus', function() {
            if (isValidDecimalFormat(this.value)) {
              lastValidValue = this.value;
            }
          });
        }
      },
      
      
      'validate-strong-password': {
        pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        message: 'Password must contain at least 8 characters, including uppercase, lowercase, number and special character.',
        setupEvents: function(element) {
          element.addEventListener('input', function() {
            
            const validator = window.nemoValidatorInstance;
            if (validator) {
              validator.validateField(this);
            }
          });
        }
      },
      
      
      'validate-age': {
        pattern: /^(1[8-9]|[2-9]\d|100)$/,
        message: 'Age must be between 18 and 100.',
        filterPattern: /[^\d]/g,
        maxLength: 3,
        setupEvents: function(element) {
          element.addEventListener('input', function() {
            
            this.value = this.value.replace(/[^\d]/g, '');
            
            
            const validator = window.nemoValidatorInstance;
            if (validator) {
              validator.validateField(this);
            }
          });
          
          
          element.addEventListener('keydown', function(e) {
            
            if (e.ctrlKey || e.altKey || e.metaKey || 
                e.key === 'Backspace' || e.key === 'Delete' ||
                e.key === 'Tab' || e.key === 'ArrowLeft' ||
                e.key === 'ArrowRight' || e.key === 'Home' ||
                e.key === 'End') {
              return;
            }
            
            
            if (!/^\d$/.test(e.key)) {
              e.preventDefault();
            }
          });
        },
        
        customValidation: function(value) {
          if (!value) return false;
          
          const age = parseInt(value);
          
          
          if (age < 18) {
            this.message = 'Age must be at least 18.';
            return false;
          }
          
          
          if (age > 100) {
            this.message = 'Age must be at most 100.';
            return false;
          }
          
          
          this.message = 'Age must be between 18 and 100.';
          return true;
        }
      },
      
      
      'validate-credit-card': {
        pattern: /^\d{13,16}$|^(?:\d{4}[ -]){3}\d{4}$|^(?:\d{4}[ -]){2}\d{4}[ -]\d{4}$/,
        message: 'Please enter a valid credit card number.',
        filterPattern: /[^\d\s-]/g,
        maxLength: 23, // Accounts for spaces/dashes (16 digits + 4 spaces)
        setupEvents: function(element) {
          
          const formatCreditCard = (value) => {
            
            value = value.replace(/\D/g, '');
            
            
            if (value.length > 16) {
              value = value.substring(0, 16);
            }
            
            if (value.length === 0) return '';
            
            
            let result = '';
            for (let i = 0; i < value.length; i++) {
              
              if (i > 0 && i % 4 === 0) {
                result += ' ';
              }
              result += value.charAt(i);
            }
            return result;
          };
          
          
          if (element.value) {
            element.value = formatCreditCard(element.value);
          }
          
          
          element.addEventListener('input', function(e) {
            const cursorPos = this.selectionStart;
            
            
            const rawValue = this.value.replace(/\s/g, '');
            const valueBeforeCursor = this.value.substring(0, cursorPos);
            const spacesBeforeCursor = (valueBeforeCursor.match(/\s/g) || []).length;
            const digitsBeforeCursor = valueBeforeCursor.replace(/\s/g, '').length;
            
            
            this.value = formatCreditCard(rawValue);
            
            
            const newSpacesBeforeCursor = Math.floor(digitsBeforeCursor / 4);
            const newCursorPos = digitsBeforeCursor + newSpacesBeforeCursor;
            
            
            this.selectionStart = this.selectionEnd = Math.min(newCursorPos, this.value.length);
            
            
            const validator = window.nemoValidatorInstance;
            if (validator) {
              validator.validateField(this);
            }
          });
          
          
          element.addEventListener('paste', function(e) {
            
            setTimeout(() => {
              this.value = formatCreditCard(this.value);
              
              this.selectionStart = this.selectionEnd = this.value.length;
              
              const validator = window.nemoValidatorInstance;
              if (validator) {
                validator.validateField(this);
              }
            }, 0);
          });
          
          
          element.addEventListener('keydown', function(e) {
            
            if (e.ctrlKey || e.altKey || e.metaKey || 
                e.key === 'Backspace' || e.key === 'Delete' ||
                e.key === 'Tab' || e.key === 'ArrowLeft' ||
                e.key === 'ArrowRight' || e.key === 'Home' ||
                e.key === 'End' || e.key === ' ') {
              return;
            }
            
            
            if (!/^\d$/.test(e.key)) {
              e.preventDefault();
            }
          });
        },
      
        customValidation: function(value) {
          if (!value) return false;
          
          
          value = value.replace(/[\s-]/g, '');
          
          
          if (value.length < 13) {
            this.message = 'Credit card number is too short.';
            return false;
          }
          
          
          if (value.length > 16) {
            this.message = 'Credit card number is too long.';
            return false;
          }
          
          
          if (!/^\d+$/.test(value)) {
            this.message = 'Credit card number can only contain digits.';
            return false;
          }
          
        
          let sum = 0;
          let shouldDouble = false;
          
          
          for (let i = value.length - 1; i >= 0; i--) {
            let digit = parseInt(value.charAt(i));
            
            if (shouldDouble) {
              digit *= 2;
              if (digit > 9) digit -= 9;
            }
            
            sum += digit;
            shouldDouble = !shouldDouble;
          }
          
          const isValid = (sum % 10) === 0;
          if (!isValid) {
            this.message = 'Invalid credit card number.';
          } else {
            this.message = 'Please enter a valid credit card number.';
          }
          
          return isValid;
        }
      },
      
      
      'validate-current-date': {
        message: 'Only the current date is allowed.',
        setupEvents: function(element) {
          
          const getTodayString = () => {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };
          
          
          const todayString = getTodayString();
          element.value = todayString;
          element.min = todayString;
          element.max = todayString;
          
          
          element.addEventListener('input', function() {
            if (this.value !== todayString) {
              this.value = todayString;
            }
          });
          
          
          element.addEventListener('keydown', function(e) {
            
            if (e.key !== 'Tab') {
              e.preventDefault();
            }
          });
          
          
          element.addEventListener('change', function() {
            this.value = todayString;
          });
        },
        customValidation: function(value) {
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          
          const parts = value.split('-');
          if (parts.length !== 3) return false;
          
          const inputDate = new Date(
            parseInt(parts[0]), 
            parseInt(parts[1]) - 1, 
            parseInt(parts[2])
          );
          inputDate.setHours(0, 0, 0, 0);
          
          
          return inputDate.getTime() === today.getTime();
        }
      }
    };

    
    if (customRules && typeof customRules === 'object') {
      this.validationRules = { ...this.validationRules, ...customRules };
    }

    
    if (typeof document !== 'undefined') {
      this.init();
    }
  }

  /**
   * Initialize validation on forms
   * @public
   */
  init() {
    this.startValidation(this.formClass);
    return this;
  }

  /**
   * Find all forms and set up validation
   * @param {string} formClass - CSS class to identify forms
   * @private
   */
  startValidation(formClass) {
    if (typeof document === 'undefined') return;

    const forms = document.getElementsByClassName(formClass);
    if (forms.length === 0) {
      return;
    }
    
    Array.from(forms).forEach((form) => {
      this.validateInputs(form);
    });
  }

  /**
   * Set up validation for inputs within a form
   * @param {HTMLElement} form - Form element to validate
   * @private
   */
  validateInputs(form) {
    if (typeof document === 'undefined') return;

    const inputs = form.querySelectorAll('[class*="validate-"]');

    inputs.forEach(input => {
      
      let errorSpan = document.getElementById(`error-message-${input.name || input.id}`);
      if (!errorSpan) {
        errorSpan = document.createElement('span');
        errorSpan.className = 'text-danger';
        errorSpan.id = `error-message-${input.name || input.id}`;

        const parentDiv = input.closest('div');
        if (parentDiv) {
          parentDiv.insertAdjacentElement('afterend', errorSpan);
        }
      }

      
      const validationTypes = Array.from(input.classList)
        .filter(className => className.startsWith('validate-'));

      if (validationTypes.length > 0) {
        
        const validationType = validationTypes[0];
        const rule = this.validationRules[validationType];
        
        if (rule && typeof rule.setupEvents === 'function') {
          
          rule.setupEvents(input);
        }
        
        input.addEventListener('input', (e) => {
          const validationType = validationTypes[0];
          const rule = this.validationRules[validationType];

          if (rule) {
            
            const filteredValue = e.target.value.replace(rule.filterPattern, '');

            
            if (rule.maxLength && filteredValue.length > rule.maxLength) {
              e.target.value = filteredValue.slice(0, rule.maxLength);
            } else {
              e.target.value = filteredValue;
            }

            
            if (validationType === 'validate-pan' || validationType === 'validate-gst' || 
                validationType === 'validate-udyam' || validationType === 'validate-uam') {
              e.target.value = e.target.value.toUpperCase();
            }

            
            const errorSpan = document.getElementById(`error-message-${input.name || input.id}`);

            if (e.target.value.trim() === '') {
              errorSpan.textContent = '';
              input.style.borderColor = '';
            } else if (!rule.pattern.test(e.target.value)) {
              errorSpan.textContent = rule.message;
              input.style.borderColor = 'red';
            } else {
              errorSpan.textContent = '';
              input.style.borderColor = '';
            }
          }
        });
      }
    });
  }

  /**
   * Validates a specific input field
   * @param {HTMLElement} input - Input element to validate
   * @returns {boolean} - Whether the input is valid
   * @public
   */
  validateField(input) {
    // Simply delegate to the main validation method, validating a single field
    return this._validateSingleField(input);
  }

  /**
   * Internal method to validate a single field
   * @param {HTMLElement} input - Input element to validate
   * @returns {boolean} - Whether the input is valid
   * @private
   */
  _validateSingleField(input) {
    if (!input || !input.classList) return true;
    
    const validationTypes = Array.from(input.classList)
      .filter(className => className.startsWith('validate-'));
    
    if (validationTypes.length === 0) return true;
    
    const validationType = validationTypes[0];
    const rule = this.validationRules[validationType];
    
    
    const errorSpan = document.getElementById(`error-message-${input.name || input.id}`);
    if (errorSpan) {
      errorSpan.textContent = '';
      input.style.borderColor = '';
    }
    
    
    if (!rule || input.value.trim() === '') {
      return true;
    }
    
    let isValid = true;
    
    
    if (rule.onValidate && typeof rule.onValidate === 'function') {
      isValid = rule.onValidate(input);
    } else if (rule.customValidation && typeof rule.customValidation === 'function') {
      isValid = rule.customValidation(input.value);
    } else if (rule.pattern) {
      
      isValid = rule.pattern.test(input.value);
    }
    
    
    if (!isValid && errorSpan) {
      errorSpan.textContent = rule.message;
      input.style.borderColor = 'red';
    }
    
    return isValid;
  }

  /**
   * Validates all inputs and returns validity status
   * @returns {boolean} - True if all inputs are valid, false otherwise
   * @public
   */
  nemoValidateInputs() {
    if (typeof document === 'undefined') return true;
    
    let hasErrors = false;
    const forms = document.getElementsByClassName(this.formClass);
    
    Array.from(forms).forEach(form => {
      const inputs = form.querySelectorAll('[class*="validate-"]');
      
      
      inputs.forEach(input => {
        const errorSpan = document.getElementById(`error-message-${input.name || input.id}`);
        if (errorSpan) {
          errorSpan.textContent = '';
          input.style.borderColor = '';
        }
      });
      
      
      inputs.forEach(input => {
        if (!this._validateSingleField(input)) {
          hasErrors = true;
        }
      });
    });
    
    return !hasErrors;
  }
  
  /**
   * Add a custom validation rule
   * @param {string} name - Rule name (e.g., 'validate-custom')
   * @param {Object} rule - Rule definition
   * @param {RegExp} rule.pattern - Pattern to test against
   * @param {string} rule.message - Error message
   * @param {RegExp} rule.filterPattern - Pattern to filter input in real-time
   * @param {number} [rule.maxLength] - Maximum length for input
   * @public
   */
  addRule(name, rule) {
    if (!name.startsWith('validate-')) {
      name = `validate-${name}`;
    }
    
    this.validationRules[name] = rule;
    return this;
  }
  
  /**
   * Remove a validation rule
   * @param {string} name - Rule name to remove
   * @public
   */
  removeRule(name) {
    if (!name.startsWith('validate-')) {
      name = `validate-${name}`;
    }
    
    delete this.validationRules[name];
    return this;
  }
  
  /**
   * Get all validation rules
   * @returns {Object} - All validation rules
   * @public
   */
  getRules() {
    return { ...this.validationRules };
  }
}


if (typeof module !== 'undefined' && module.exports) {
  module.exports = NemoValidator;
} else if (typeof define === 'function' && define.amd) {
  define([], function() {
    return NemoValidator;
  });
} else if (typeof window !== 'undefined') {
  window.NemoValidator = NemoValidator;
}

export default NemoValidator;
